// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { fetchFamilyDataPoints } from '../../services/api';
import { fetchFamilyDescriptions } from '../../utils/familyDescriptions';
import { canonicalSubject } from '../../utils/progressStatus';
import { fetchAllVocabBanks, HighlightVocabText } from '../../utils/vocabBank';
import { dataPointTypeLabel } from '../../utils/dataPointType';
import { EvidenceAttachment } from '../../components/DataPointDots';
import { GRADING_STYLES, pointColor } from '../../utils/grading';
import './ViewData.css';
import '../../components/DataSheet.css';
import AdditionalSupportIndicator from '../../components/AdditionalSupportIndicator';

function attachmentsFor(row) {
  const attachments = [...(row.evidence_files || [])];
  if (row.evidence && !attachments.some((attachment) => attachment.filename === row.evidence)) {
    attachments.unshift({ filename: row.evidence, name: 'uploaded evidence' });
  }
  return attachments;
}

export default function ViewData() {
  const [rows, setRows] = useState<any>([]);
  const [vocabBanks, setVocabBanks] = useState<any>({});
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [descriptionError, setDescriptionError] = useState<any>('');
  const [reload, setReload] = useState<any>(0);
  const [studentId, setStudentId] = useState<any>('');
  const [subject, setSubject] = useState<any>('');
  const [search, setSearch] = useState<any>('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setDescriptionError('');
    async function load() {
      try {
        const [data, descriptions] = await Promise.all([
          fetchFamilyDataPoints(),
          fetchFamilyDescriptions().catch((loadError) => {
            if (!cancelled) setDescriptionError(`${loadError.message} Standard descriptions are shown instead.`);
            return {};
          }),
        ]);
        if (!cancelled) {
          setRows(data.data_points.map((row) => ({
            ...row,
            subject: canonicalSubject(row.subject || descriptions[row.standard_code]?.subject || ''),
            standard_description: descriptions[row.standard_code]?.standard || row.standard_description || row.standard_code || 'Standard',
          })));
        }
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Could not load data points.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    fetchAllVocabBanks()
      .then((banks) => {
        if (!cancelled) setVocabBanks(banks);
      })
      .catch((loadError) => {
        if (!cancelled) console.error('Could not load vocabulary banks.', loadError);
      });
    return () => { cancelled = true; };
  }, [reload]);

  const students = useMemo(() => [...new Map(rows.map((row) => [
    String(row.student_id), row.student_name,
  ])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [rows]);
  const subjects = useMemo(() => [...new Set(rows.map((row) => row.subject).filter(Boolean))].sort(), [rows]);
  const filtered = rows.filter((row) => (
    (!studentId || String(row.student_id) === studentId)
    && (!subject || row.subject === subject)
    && (!search.trim() || [row.title, row.standard_code, row.standard_description, row.notes]
      .some((value) => String(value || '').toLowerCase().includes(search.trim().toLowerCase())))
  ));
  const groups = new Map();
  filtered.forEach((row) => {
    if (!groups.has(row.student_id)) groups.set(row.student_id, { name: row.student_name, rows: [] });
    groups.get(row.student_id).rows.push(row);
  });

  return (
    <div className="family-view-data">
      <div className="ae-page-hero">
        <h1>View Data</h1>
        <p>Review your children&apos;s data points, shared notes, and evidence.</p>
      </div>
      {descriptionError && <div role="alert" className="family-data-warning">{descriptionError}</div>}
      {loading ? <p role="status">Loading data points...</p> : error ? (
        <div role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => setReload((value) => value + 1)}>Retry</button>
        </div>
      ) : (
        <>
          <div className="family-data-filters">
            <label>Student
              <select value={studentId} onChange={(event) => setStudentId(event.target.value)}>
                <option value="">All students</option>
                {students.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </label>
            <label>Subject
              <select value={subject} onChange={(event) => setSubject(event.target.value)}>
                <option value="">All subjects</option>
                {subjects.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <label>Search
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search titles, standards, or shared notes" />
            </label>
            <button type="button" onClick={() => { setStudentId(''); setSubject(''); setSearch(''); }}>Clear filters</button>
          </div>
          <p role="status">Showing {filtered.length} data point{filtered.length === 1 ? '' : 's'}</p>
          {!filtered.length && <p>No data points match the selected filters.</p>}
          {[...groups].map(([id, group]) => (
            <section key={id} className="family-data-group" aria-label={`${group.name} data points`}>
              <h2>{group.name}</h2>
              <div className="family-data-table-scroll data-sheet-container" role="region" aria-label={`${group.name} data point list`} tabIndex={0}>
                <table className="data-sheet" role="table">
                  <caption className="family-data-caption">Data points for {group.name}</caption>
                  <thead>
                    <tr role="row">{['Standard', 'Subject', 'Standard Grade', 'Date', 'Title', 'Type', 'Status', 'Author', 'Public Notes', 'Evidence'].map((heading) => (
                      <th role="columnheader" scope="col" key={heading}>{heading}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row) => {
                      const gradingStyle = GRADING_STYLES[pointColor(row)] || GRADING_STYLES.gray;
                      return (
                      <tr key={row.id} role="row" className={row.accommodations_applied ? 'ae-supported-data-point' : undefined}>
                        <td role="cell" data-label="Standard" className="family-data-description">
                          <HighlightVocabText
                            text={row.standard_description}
                            subject={row.subject}
                            vocabBank={vocabBanks[canonicalSubject(row.subject)]}
                            active={true}
                          />
                          <small>{row.standard_code}</small>
                        </td>
                        <td role="cell" data-label="Subject" className="family-data-subject">{row.subject || '-'}</td>
                        <td role="cell" data-label="Standard Grade" className="family-data-detail">{row.grade_level || '-'}</td>
                        <td role="cell" data-label="Date" className="family-data-detail">{row.date || '-'}</td>
                        <td role="cell" data-label="Title" className="family-data-detail">
                          <div className="ae-data-point-header">
                            <span>{row.title || '-'}</span>
                            <AdditionalSupportIndicator applied={row.accommodations_applied} />
                          </div>
                        </td>
                        <td role="cell" data-label="Type" className="family-data-detail">{dataPointTypeLabel(row.data_type)}</td>
                        <td role="cell" data-label="Status" className="family-data-detail">{row.score_hidden ? 'Hidden by school' : (
                          <span className="data-sheet-status-badge" title={gradingStyle.label}
                            style={{ background: gradingStyle.bg, color: gradingStyle.text }}>
                            {gradingStyle.label}
                          </span>
                        )}</td>
                        <td role="cell" data-label="Author" className="family-data-teacher">{row.teacher_name || '-'}</td>
                        <td role="cell" data-label="Public Notes" className="family-data-notes">{row.notes || '-'}</td>
                        <td role="cell" data-label="Evidence">{attachmentsFor(row).length ? attachmentsFor(row).map((attachment) => (
                          <EvidenceAttachment key={attachment.filename} attachment={attachment} dataPointId={row.id}
                            linkColor="var(--ae-primary-strong)" errorColor="#b91c1c" />
                        )) : '-'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
